import { Injectable, ElementRef, Renderer2 } from '@angular/core';
import { BaseMapProvider, MapProvider, MarkerOptions, RouteRequest, RouteResult } from './map-provider.service';
import { RouteConsistencyService } from './route-consistency.service';
import { osService } from '../osService.service';
import * as L from 'leaflet';
import 'leaflet-routing-machine';

@Injectable({
  providedIn: 'root'
})
export class OpenStreetMapProviderService extends BaseMapProvider {
  private routingControl: L.Routing.Control | null = null;
  private elementRef: ElementRef | null = null;
  private renderer: Renderer2 | null = null;

  constructor(
    private osService: osService,
    private routeConsistencyService: RouteConsistencyService
  ) {
    super();
  }

  setElementRef(elementRef: ElementRef, renderer: Renderer2): void {
    this.elementRef = elementRef;
    this.renderer = renderer;
  }

  async initializeMap(element: HTMLElement, options: any): Promise<any> {
    const container = this.map?.getContainer?.();
    const mapAttachedToElement =
      !!container && element.contains(container);

    if (this.map && !mapAttachedToElement) {
      this.destroyMap(this.map);
    }

    if (!this.map) {
      this.map = L.map(element, { zoomControl: false }).setView(
        options.center,
        options.zoom
      );
      this.setupTileLayer(options);
    } else {
      this.setMapCenter(this.map, options.center);
    }
    return this.map;
  }

  private setupTileLayer(options: any): void {
    let tileLayerUrl: string;
    let attribution: string;

    if (options.map_layer === "MAPBOX" && options.map_layer_key) {
      const streetLayer = L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${options.map_layer_key}`,
        {
          maxZoom: 19,
          tileSize: 1024,
          zoomOffset: -2,
          attribution: "© Mapbox © OpenStreetMap contributors",
        }
      );

      const satelliteLayer = L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=${options.map_layer_key}`,
        {
          maxZoom: 19,
          tileSize: 1024,
          zoomOffset: -2,
          attribution: "© Mapbox © OpenStreetMap contributors",
        }
      );

      if (options.modeSatellite) {
        satelliteLayer.addTo(this.map);
      } else {
        streetLayer.addTo(this.map);
      }
    } else {
      tileLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
      attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
      L.tileLayer(tileLayerUrl, {
        maxZoom: 19,
        attribution: attribution,
      }).addTo(this.map);
    }
  }

  createMarker(position: any, options: MarkerOptions = {}): any {
    const latLng = L.latLng(position.lat || position.latitude, position.lng || position.longitude);
    
    let icon: L.Icon;
    if (options.icon) {
      // options.icon peut être soit une chaîne (URL), soit un objet Leaflet icon config
      if (typeof options.icon === 'string') {
        // Si c'est une chaîne, créer une icône avec cette URL
        icon = L.icon({
          iconUrl: options.icon,
          iconSize: [options.iconSize?.width || 40, options.iconSize?.height || 40],
          iconAnchor: [options.iconAnchor?.x || 20, options.iconAnchor?.y || 20],
        });
      } else if (options.icon && typeof options.icon === 'object' && options.icon.iconUrl) {
        // Si c'est un objet de configuration Leaflet (retourné par getLeafletIcon)
        // S'assurer que toutes les propriétés sont correctement extraites
        const iconConfig = options.icon;
        icon = L.icon({
          iconUrl: iconConfig.iconUrl,
          iconSize: Array.isArray(iconConfig.iconSize) ? iconConfig.iconSize : [40, 40],
          iconAnchor: Array.isArray(iconConfig.iconAnchor) ? iconConfig.iconAnchor : [20, 20],
          popupAnchor: Array.isArray(iconConfig.popupAnchor) ? iconConfig.popupAnchor : [0, -20],
        });
      } else {
        // Fallback sur l'icône par défaut
        icon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
        });
      }
    } else {
      icon = L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
      });
    }

    const marker = L.marker(latLng, { icon });
    marker.addTo(this.map);
    this.addMarker(marker);
    return marker;
  }

  removeMarker(marker: any): void {
    if (marker && this.map.hasLayer(marker)) {
      this.map.removeLayer(marker);
      this.removeMarkerFromList(marker);
    }
  }

  clearMarkers(markers: any[]): void {
    markers.forEach(marker => this.removeMarker(marker));
  }

  async calculateRoute(request: RouteRequest): Promise<RouteResult> {
    return new Promise((resolve, reject) => {
      // Normalisation et validation de la requête
      const normalizedRequest = this.routeConsistencyService.normalizeRouteRequest(request);
      if (!this.routeConsistencyService.validateRouteRequest(normalizedRequest)) {
        reject(new Error('Invalid route request'));
        return;
      }

      // Log pour le débogage
      this.routeConsistencyService.logRouteRequest(normalizedRequest, 'OpenStreetMap');

      if (this.routingControl && this.map) {
        this.map.removeControl(this.routingControl);
      }

      this.defineRoutePoints(request);
      
      if (this.routingControl && this.map) {
        setTimeout(() => {
          if (this.routingControl && this.map) {
            this.routingControl.on("routesfound", (e: any) => {
              const route = e.routes[0];
              const distance = this.osService.standardizeDistance(route.summary.totalDistance);
              
              let totalDurationInSeconds = route.summary.totalTime;
              const hours = Math.floor(totalDurationInSeconds / 3600);
              const minutes = Math.floor((totalDurationInSeconds % 3600) / 60);

              const routeResult: RouteResult = {
                distance,
                duration: { hours, minutes },
                route: e.routes
              };

              // Validation et log du résultat
              this.routeConsistencyService.validateRouteResult(routeResult, normalizedRequest.origin, normalizedRequest.destination);
              this.routeConsistencyService.logRouteResult(routeResult, 'OpenStreetMap');

              this.hideRoutingContainer();
              resolve(routeResult);
            });

            this.routingControl.on("routingerror", (e: any) => {
              reject(new Error("Route calculation failed"));
            });
          }
        }, 110);
      }
    });
  }

  private defineRoutePoints(request: RouteRequest): void {
    const waypoints: L.LatLng[] = [];
    
    // Normalisation des coordonnées
    const origin = this.routeConsistencyService.normalizeCoordinates(request.origin);
    const destination = this.routeConsistencyService.normalizeCoordinates(request.destination);
    
    if (request.waypoints && request.waypoints.length) {
      waypoints.push(
        L.latLng(origin.lat, origin.lng),
        ...request.waypoints.map(wp => {
          const wpCoords = this.routeConsistencyService.normalizeCoordinates(wp.location || wp);
          return L.latLng(wpCoords.lat, wpCoords.lng);
        }),
        L.latLng(destination.lat, destination.lng)
      );
    } else {
      waypoints.push(
        L.latLng(origin.lat, origin.lng),
        L.latLng(destination.lat, destination.lng)
      );
    }

    // Configuration des options de routage avec paramètres standardisés
    const routingOptions: any = {
      waypoints: waypoints,
      routeWhileDragging: true,
      show: false, // Ne pas afficher le panneau d'instructions
      fitSelectedRoutes: false,
      addWaypoints: false, // Désactiver l'ajout de waypoints
      createMarker: (i: number, waypoint: any, n: number) => {
        // Créer des marqueurs invisibles pour éviter les conflits avec les marqueurs personnalisés
        // Les marqueurs personnalisés sont gérés par le composant
        const invisibleIcon = L.divIcon({
          className: 'leaflet-routing-icon-hide',
          iconSize: [0, 0],
          iconAnchor: [0, 0],
          html: ''
        });
        const marker = L.marker(waypoint.latLng, { 
          icon: invisibleIcon,
          interactive: false // Désactiver l'interaction pour éviter les conflits
        });
        return marker;
      },
      // Paramètres pour correspondre aux options Google Maps
      lineOptions: {
        styles: [
          { color: '#4088CD', opacity: 1, weight: 4 }
        ]
      }
    };

    // Note: Les paramètres d'évitement spécifiques ne sont pas supportés par leaflet-routing-machine
    // mais la cohérence est maintenue via les paramètres de base

    this.routingControl = L.Routing.control(routingOptions).addTo(this.map);
    
    // Cacher immédiatement le conteneur d'instructions
    setTimeout(() => {
      this.hideRoutingContainer();
    }, 0);
    
    // Ajouter un style CSS global pour cacher définitivement les instructions
    if (this.elementRef && this.renderer) {
      const style = document.createElement('style');
      style.textContent = `
        .leaflet-routing-container,
        .leaflet-routing-instructions,
        .leaflet-routing-geocoders {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  private getMarkerIcon(index: number, total: number): L.Icon {
    const startIcon = L.icon({
      iconUrl: "/assets/icon/car-maps.svg",
      iconSize: [70, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
    });

    const endIcon = L.icon({
      iconUrl: "/assets/icon/house.svg",
      iconSize: [70, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
    });

    if (index === 0) {
      return startIcon;
    } else if (index === total - 1) {
      return endIcon;
    } else {
      return L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
      });
    }
  }

  private hideRoutingContainer(): void {
    if (this.elementRef && this.renderer) {
      // Cacher tous les conteneurs de routing possibles
      const selectors = [
        ".leaflet-routing-container",
        ".leaflet-routing-instructions",
        ".leaflet-routing-geocoders"
      ];
      
      selectors.forEach(selector => {
        const elements = this.elementRef!.nativeElement.querySelectorAll(selector);
        elements.forEach((element: HTMLElement) => {
          this.renderer!.setStyle(element, "display", "none");
          this.renderer!.setStyle(element, "visibility", "hidden");
        });
      });
      
      // Cacher aussi via le routingControl si disponible
      if (this.routingControl && (this.routingControl as any).getContainer) {
        const container = (this.routingControl as any).getContainer();
        if (container) {
          this.renderer!.setStyle(container, "display", "none");
          this.renderer!.setStyle(container, "visibility", "hidden");
        }
      }
    }
  }

  updateMarkerPosition(marker: any, position: any): void {
    if (marker) {
      const newLatLng = L.latLng(position.lat || position.latitude, position.lng || position.longitude);
      marker.setLatLng(newLatLng);
    }
  }

  setMapCenter(map: any, center: any): void {
    if (map) {
      const latLng = L.latLng(center.lat, center.lng);
      map.setView(latLng);
    }
  }

  destroyMap(map: any): void {
    if (this.routingControl && this.map) {
      this.map.removeControl(this.routingControl);
      this.routingControl = null;
    }
    this.clearAllMarkers();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  // Méthodes spécifiques à OpenStreetMap
  calculateBearing(startLat: number, startLng: number, endLat: number, endLng: number): number {
    const startPoint = L.latLng(startLat, startLng);
    const endPoint = L.latLng(endLat, endLng);
    const dx = endPoint.lng - startPoint.lng;
    const dy = endPoint.lat - startPoint.lat;
    const angle = Math.atan2(dy, dx);
    return angle * (180 / Math.PI);
  }
} 